import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'localization';
import NotificationBell from '../NotificationBell/NotificationBell';
import './MobileNotificationBellMolecule.scss';

interface MobileNotificationBellProps {
  className?: string;
  onViewAllClick?: () => void; // Callback when "View all notifications" is clicked
  isActive?: boolean; // Whether the notification icon should show active state
}

/**
 * Mobile-optimized wrapper for NotificationBell
 * Renders as full-screen modal on mobile devices instead of dropdown
 */
const MobileNotificationBellMolecule: React.FC<MobileNotificationBellProps> = ({
  className = '',
  onViewAllClick,
  isActive = false
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bellKey, setBellKey] = useState(0);
  const modalBellRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false); // Track if we're in the process of closing

  const handleBellClick = () => {
    // Increment key when opening to ensure fresh state
    setBellKey(prev => prev + 1);
    setIsModalOpen(true);
  };

  const handleCloseModal = (e?: React.MouseEvent) => {
    // Prevent event propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Set closing flag to prevent reopening
    isClosingRef.current = true;

    // Immediately close modal
    setIsModalOpen(false);

    // Reset closing flag after a delay
    setTimeout(() => {
      isClosingRef.current = false;
    }, 500);
  };

  const handleMenuStateChange = (isOpen: boolean) => {
    // Don't reopen if we're in the process of closing
    if (isClosingRef.current) {
      return;
    }

    // Prevent desktop dropdown from opening on mobile
    // Only trigger modal open if it's not already open
    if (isOpen && window.innerWidth < 500 && !isModalOpen) {
      setIsModalOpen(true);
    }
  };

  const handleNotificationClick = () => {
    // Close modal when a notification is clicked
    handleCloseModal();
  };

  const handleViewAllClick = () => {
    // Close modal and call parent callback
    handleCloseModal();
    if (onViewAllClick) {
      onViewAllClick();
    }
  };

  // Auto-click the notification bell when modal opens to show dropdown
  useEffect(() => {
    if (isModalOpen && modalBellRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const bellButton = modalBellRef.current?.querySelector('.notification-bell');
        if (bellButton) {
          (bellButton as HTMLButtonElement).click();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isModalOpen]);

  return (
    <>
      {/* Bell Icon Button for Mobile Nav Bar */}
      <div className={`mobile-notification-bell ${className}`}>
        <NotificationBell
          onNotificationClick={handleBellClick}
          onMenuStateChange={handleMenuStateChange}
          className="mobile-notification-bell__trigger"
          isActive={isActive}
        />
      </div>

      {/* Full-Screen Modal for Mobile */}
      {isModalOpen && (
        <dialog
          className="mobile-notification-modal"
          open
          aria-labelledby="notification-modal-title"
        >
          <button
            type="button"
            className="mobile-notification-modal__overlay"
            aria-label={t('notifications.close')}
            onClick={handleCloseModal}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCloseModal();
              }
            }}
          />
          <main className="mobile-notification-modal__content">
            <header className="mobile-notification-modal__header">
              <h2 id="notification-modal-title">{t('notifications.title')}</h2>
              <button
                className="mobile-notification-modal__close"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCloseModal(e);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                aria-label={t('notifications.close')}
                type="button"
              >
                <i className="pi pi-times" />
              </button>
            </header>
            <div className="mobile-notification-modal__body" ref={modalBellRef}>
              <NotificationBell
                key={bellKey}
                onNotificationClick={handleNotificationClick}
                onViewAllClick={handleViewAllClick}
                className="mobile-notification-bell__fullview"
              />
            </div>
          </main>
        </dialog>
      )}
    </>
  );
};

export default MobileNotificationBellMolecule;
